import { push } from 'react-router-redux';
import { Reducer } from 'redux';
import { AppDispatch, RootState } from '../appStore';
import { IHasValidationModel, ISurveyNote, SurveyStep } from '../models';
import { SurveyBuilderHelper } from '../utilities';
import { IApplicationState } from './';
import * as Actions from './Actions';
import { ActionTypes } from './ActionTypes';

export interface ISurveyBuilderState extends IHasValidationModel {
	surveyId: number;
	saving: boolean;
	currentStep: SurveyStep;
	openWarningModal: boolean;
	hasUnsavedChanges: boolean;
	requestedRoutingAction?: 'next' | 'previous' | 'jump' | 'uncontrolled';
	destinationStep?: SurveyStep;
	destinationPath?: string;
	notesLoading: boolean;
	noteSaving: boolean;
	notes: ISurveyNote[];
}

export const defaultState: ISurveyBuilderState = {
	surveyId: 0,
	saving: false,
	currentStep: SurveyStep.Basics,
	openWarningModal: false,
	hasUnsavedChanges: false,
	isValid: true,
	notesLoading: false,
	noteSaving: false,
	notes: [],
};

export const ActionCreators = {
	Save: (advance: boolean) => (dispatch: AppDispatch, getState: () => IApplicationState) =>
		SurveyBuilderHelper.doSave(dispatch, getState(), advance),
	PreviousStep: () => (dispatch: AppDispatch, getState: () => RootState) => {
		const { hasUnsavedChanges, currentStep, surveyId } = getState().surveyBuilder;
		if (hasUnsavedChanges) {
			dispatch({
				type: ActionTypes.STEPPER_ROUTING_REQUESTED,
				payload: {
					openWarningModal: true,
					requestedRoutingAction: 'previous',
				},
			});
		} else {
			SurveyBuilderHelper.goToPreviousStep(dispatch, currentStep, surveyId);
		}
	},
	NextStep: () => (dispatch: AppDispatch, getState: () => RootState) => {
		const { hasUnsavedChanges, currentStep, surveyId } = getState().surveyBuilder;
		if (hasUnsavedChanges) {
			dispatch({
				type: ActionTypes.STEPPER_ROUTING_REQUESTED,
				payload: {
					requestedRoutingAction: 'next',
					openWarningModal: true,
				},
			});
		} else {
			SurveyBuilderHelper.goToNextStep(dispatch, currentStep, surveyId);
		}
	},
	Jump: (destinationStep: SurveyStep) => (dispatch: AppDispatch, getState: () => RootState) => {
		const { hasUnsavedChanges, surveyId } = getState().surveyBuilder;
		if (hasUnsavedChanges) {
			dispatch({
				type: ActionTypes.STEPPER_ROUTING_REQUESTED,
				payload: {
					openWarningModal: true,
					requestedRoutingAction: 'jump',
					destinationStep,
				},
			});
		} else {
			SurveyBuilderHelper.route(dispatch, destinationStep, surveyId);
		}
	},
	OpenSurvey: (surveyId: number, destinationStep: SurveyStep, newWindow?: boolean) => (dispatch: AppDispatch) => {
		SurveyBuilderHelper.route(dispatch, destinationStep, surveyId, newWindow);
	},
	InitializePage: (surveyId: number, currentStep: SurveyStep) => ({
		type: ActionTypes.SURVEY_BUILDER_INITIALIZE,
		payload: { surveyId, currentStep },
	}),
	CheckForUnsavedChanges: () => (dispatch: AppDispatch, getState: () => IApplicationState) => {
		const hasUnsavedChanges = SurveyBuilderHelper.hasUnsavedChanges(getState());
		if (getState().surveyBuilder.hasUnsavedChanges !== hasUnsavedChanges) {
			dispatch({ type: ActionTypes.STEPPER_UNSAVED_CHANGES_ANALYSIS, payload: hasUnsavedChanges });
		}
	},
	RequestRouting: (route: string, openWarningModal?: boolean) => (dispatch: AppDispatch) => {
		dispatch({
			type: ActionTypes.STEPPER_ROUTING_REQUESTED,
			payload: {
				requestedRoutingAction: 'uncontrolled',
				destinationPath: route,
				openWarningModal,
			},
		});
	},
	WarningModalCancel: () => ({ type: ActionTypes.STEPPER_ROUTING_CANCELED }),
	WarningModalContinue: () => (dispatch: AppDispatch, getState: () => IApplicationState) => {
		const { currentStep, requestedRoutingAction, destinationStep, destinationPath, surveyId } = getState().surveyBuilder;

		dispatch({ type: ActionTypes.STEPPER_ROUTING_DISCARD_CHANGES });
		switch (requestedRoutingAction) {
			case 'next':
				SurveyBuilderHelper.goToNextStep(dispatch, currentStep, surveyId);
				break;
			case 'previous':
				SurveyBuilderHelper.goToPreviousStep(dispatch, currentStep, surveyId);
				break;
			case 'jump':
				if (destinationStep) {
					SurveyBuilderHelper.route(dispatch, destinationStep, surveyId);
				}
				break;
			default:
				if (destinationPath) {
					dispatch(push(destinationPath));
				}
		}
	},
	GoToSurveyList: () => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.SURVEY_BUILDER_RESET });
		dispatch(push('/'));
	},
};

type KnownActions =
	| Actions.ISurveyBuilderInitialize
	| Actions.ISurveyBuilderReset
	| Actions.IStepperRoutingComplete
	| Actions.IStepperSaveComplete
	| Actions.IStepperSaveFailed
	| Actions.IStepperRoutingRequested
	| Actions.IStepperRoutingCanceled
	| Actions.IStepperRoutingDiscardChanges
	| Actions.IStepperUnsavedChangesAnalysis
	| Actions.IToggleWarningModal
	| Actions.IStepperSaveValidationFailed
	| Actions.IStepperValidationCleared
	| Actions.IStepperSaveRequested
	| Actions.ISurveyGetSucceeded
	| Actions.ISurveyNotesGetRequested
	| Actions.ISurveyNotesGetSucceeded
	| Actions.ISurveyNoteSaveRequested
	| Actions.ISurveyNoteSaveSucceeded
	| Actions.ISurveyNoteDeleteRequested
	| Actions.ISurveyNoteDeleteSucceeded;

export const reducer: Reducer<ISurveyBuilderState, KnownActions> = (
	state: ISurveyBuilderState | undefined,
	action: KnownActions,
): ISurveyBuilderState => {
	if (!state) {
		state = { ...defaultState };
	}

	switch (action.type) {
		case ActionTypes.SURVEY_BUILDER_INITIALIZE:
			return { ...state, ...action.payload };

		case ActionTypes.SURVEY_BUILDER_RESET:
			return { ...state, ...defaultState };

		// SAVING
		case ActionTypes.STEPPER_SAVE_REQUESTED:
			return { ...state, saving: true };

		case ActionTypes.SURVEY_GET_SUCCEEDED:
			return {
				...state,
				saving: false,
				hasUnsavedChanges: false,
				isValid: action.payload.isValid,
				validationResults: action.payload.validationResults,
			};
		case ActionTypes.STEPPER_SAVE_COMPLETE:
			return {
				...state,
				saving: false,
				hasUnsavedChanges: false,
				isValid: action.payload.result.isValid,
				validationResults: action.payload.result.validationResults,
				surveyId: action.payload.surveyId,
			};
		case ActionTypes.STEPPER_SAVE_FAILED:
		case ActionTypes.STEPPER_VALIDATION_FAILED:
			return {
				...state,
				saving: false,
				isValid: action.payload.isValid,
				validationResults: action.payload.validationResults,
			};
		case ActionTypes.STEPPER_VALIDATION_CLEARED:
			return {
				...state,
				isValid: true,
				validationResults: undefined,
			};
		case ActionTypes.STEPPER_ROUTING_COMPLETE:
			return {
				...state,
				currentStep: action.payload,
			};

		// PRE-SAVING
		case ActionTypes.STEPPER_UNSAVED_CHANGES_ANALYSIS:
			return {
				...state,
				hasUnsavedChanges: action.payload,
			};

		// ROUTING
		case ActionTypes.STEPPER_ROUTING_REQUESTED:
			return {
				...state,
				...action.payload,
			};
		case ActionTypes.STEPPER_ROUTING_DISCARD_CHANGES:
			return {
				...state,
				validationResults: undefined,
				requestedRoutingAction: undefined,
				destinationStep: undefined,
				destinationPath: undefined,
				openWarningModal: false,
				hasUnsavedChanges: false,
			};
		case ActionTypes.STEPPER_ROUTING_CANCELED:
			return {
				...state,
				requestedRoutingAction: undefined,
				destinationStep: undefined,
				destinationPath: undefined,
				openWarningModal: false,
			};

		// SURVEY NOTES
		case ActionTypes.SURVEY_NOTES_GET_REQUESTED:
		case ActionTypes.SURVEY_NOTE_SAVE_REQUESTED:
		case ActionTypes.SURVEY_NOTE_DELETE_REQUESTED:
			return {
				...state,
				notesLoading: true,
			};
		case ActionTypes.SURVEY_NOTES_GET_SUCCEEDED:
		case ActionTypes.SURVEY_NOTE_SAVE_SUCCEEDED:
		case ActionTypes.SURVEY_NOTE_DELETE_SUCCEEDED:
			return {
				...state,
				notes: action.payload,
				notesLoading: false,
			};
	}
	return state;
};
